import React, { useState } from 'react';

const Standard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // 센서별 표준 데이터
  const sensorStandards = {
    lidar: {
      name: "LiDAR 센서",
      icon: "🌐",
      color: "#10b981",
      topics: [
        "/velodyne_points",
        "/ouster/points", 
        "/livox/lidar",
        "/hesai/pandar",
        "/points_raw"
      ],
      messageTypes: [
        "sensor_msgs/PointCloud2",
        "sensor_msgs/PointCloud",
        "velodyne_msgs/VelodyneScan"
      ],
      specifications: {
        pointDensity: "30,000-100,000 points/frame",
        frequency: "10-20 Hz",
        rangeAccuracy: "±2-5 cm",
        angularResolution: "0.1-0.4°"
      },
      dataStructure: {
        pointFields: [
          { name: "x", type: "FLOAT32", description: "X 좌표 (m)" },
          { name: "y", type: "FLOAT32", description: "Y 좌표 (m)" },
          { name: "z", type: "FLOAT32", description: "Z 좌표 (m)" },
          { name: "intensity", type: "FLOAT32", description: "반사 강도" },
          { name: "ring", type: "UINT16", description: "레이저 링 번호" },
          { name: "time", type: "FLOAT32", description: "상대적 시간" }
        ]
      }
    },
    radar: {
      name: "Radar 센서",
      icon: "📡",
      color: "#f59e0b",
      topics: [
        "/radar/front/tracks",
        "/radar/rear/tracks",
        "/continental_radar/objects",
        "/delphi_esr/tracks"
      ],
      messageTypes: [
        "radar_msgs/RadarTrackArray",
        "radar_msgs/RadarDetectionArray",
        "automotive_radar_msgs/RadarDetectionArray"
      ],
      specifications: {
        detectionRange: "0.5-250 m",
        azimuthCoverage: "±60°",
        elevationCoverage: "±10°",
        velocityRange: "±60 m/s"
      },
      dataStructure: {
        trackFields: [
          { name: "id", type: "uint32", description: "고유 추적 ID" },
          { name: "position", type: "geometry_msgs/Point", description: "3D 위치 (m)" },
          { name: "velocity", type: "geometry_msgs/Vector3", description: "속도 (m/s)" },
          { name: "rcs", type: "float32", description: "Radar Cross Section (dBsm)" },
          { name: "snr", type: "float32", description: "Signal-to-Noise Ratio (dB)" }
        ]
      }
    },
    camera: {
      name: "Camera 센서",
      icon: "📷",
      color: "#3b82f6",
      topics: [
        "/camera/front/image_raw",
        "/camera/rear/image_raw",
        "/usb_cam/image_raw",
        "/camera/front/image_color"
      ],
      messageTypes: [
        "sensor_msgs/Image",
        "sensor_msgs/CompressedImage"
      ],
      specifications: {
        resolution: ["640x480", "1280x720", "1920x1080", "3840x2160"],
        framerate: "15-60 fps",
        bitDepth: "8-16 bit"
      },
      dataStructure: {
        imageFields: [
          { name: "width", type: "uint32", description: "이미지 너비 (pixels)" },
          { name: "height", type: "uint32", description: "이미지 높이 (pixels)" },
          { name: "encoding", type: "string", description: "인코딩 형식" },
          { name: "data", type: "uint8[]", description: "이미지 데이터" }
        ]
      }
    },
    imu: {
      name: "IMU 센서",
      icon: "🧭",
      color: "#8b5cf6",
      topics: [
        "/imu/data",
        "/imu/data_raw",
        "/xsens/imu/data",
        "/vectornav/imu"
      ],
      messageTypes: [
        "sensor_msgs/Imu",
        "sensor_msgs/MagneticField"
      ],
      specifications: {
        frequency: "100-1000 Hz",
        gyroRange: "±250 to ±2000 °/s",
        accelRange: "±2g to ±16g",
        biasStability: "< 10 °/h"
      },
      dataStructure: {
        imuFields: [
          { name: "orientation", type: "geometry_msgs/Quaternion", description: "자세 (쿼터니언)" },
          { name: "angular_velocity", type: "geometry_msgs/Vector3", description: "각속도 (rad/s)" },
          { name: "linear_acceleration", type: "geometry_msgs/Vector3", description: "선형가속도 (m/s²)" }
        ]
      }
    },
    gps: {
      name: "GPS/GNSS 센서",
      icon: "🛰️",
      color: "#ec4899",
      topics: [
        "/gps/fix",
        "/gnss/fix",
        "/navsat/fix",
        "/ublox_gps/fix"
      ],
      messageTypes: [
        "sensor_msgs/NavSatFix",
        "gps_common/GPSFix"
      ],
      specifications: {
        horizontalAccuracy: "1-5 m",
        verticalAccuracy: "2-10 m",
        frequency: "1-10 Hz"
      },
      dataStructure: {
        gpsFields: [
          { name: "latitude", type: "float64", description: "위도 (degrees)" },
          { name: "longitude", type: "float64", description: "경도 (degrees)" },
          { name: "altitude", type: "float64", description: "고도 (meters)" },
          { name: "status", type: "NavSatStatus", description: "GPS 수신 상태" }
        ]
      }
    }
  };

  const qualityStandards = {
    temporal: {
      timestampFormat: "ROS Time (sec.nsec)",
      syncTolerance: "±10 ms",
      dropoutThreshold: "< 5%",
      frequencyStability: "±10%"
    },
    spatial: {
      transformationAccuracy: "±1 cm, ±0.1°",
      calibrationValidity: "< 6 months",
      coverageRequirement: "≥ 95%"
    },
    signal: {
      lidar: { minPoints: 10000, maxNoise: 0.05 },
      radar: { minSNR: "10 dB", maxFalseAlarm: 0.01 },
      camera: { minContrast: 0.3, maxBlur: 0.2 },
      imu: { biaStability: "< 1°/h" },
      gps: { minSatellites: 6, maxHDOP: 3.0 }
    }
  };

  const namingConvention = {
    pattern: "{location}_{scenario}_{vehicle}_{date}_{time}_{sequence}.bag",
    examples: [
      "highway_cruise_vehicle01_20240115_143052_001.bag",
      "urban_intersection_testcar_20240115_091234_002.bag",
      "parking_maneuver_autonomous_20240115_160945_003.bag"
    ]
  };

  const TabButton = ({ id, label, icon }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`tab-button ${activeTab === id ? 'active' : ''}`}
    >
      <span className="tab-icon">{icon}</span>
      {label}
    </button>
  );

  const SensorCard = ({ sensor, data }) => (
    <div className="sensor-card" style={{ borderLeftColor: data.color }}>
      <div className="sensor-header">
        <span className="sensor-icon" style={{ color: data.color }}>
          {data.icon}
        </span>
        <h3>{data.name}</h3>
      </div>
      
      <div className="sensor-content">
        <div className="info-section">
          <h4>주요 토픽</h4>
          <div className="topic-list">
            {data.topics.map((topic, index) => (
              <code key={index} className="topic-name">{topic}</code>
            ))}
          </div>
        </div>
        
        <div className="info-section">
          <h4>메시지 타입</h4>
          <div className="message-types">
            {data.messageTypes.map((type, index) => (
              <span key={index} className="message-type">{type}</span>
            ))}
          </div>
        </div>
        
        <div className="info-section">
          <h4>기술 사양</h4>
          <div className="specifications">
            {Object.entries(data.specifications).map(([key, value]) => (
              <div key={key} className="spec-item">
                <span className="spec-label">{key}:</span>
                <span className="spec-value">
                  {Array.isArray(value) ? value.join(', ') : value}
                </span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="info-section">
          <button 
            className="expand-button"
            onClick={() => toggleSection(`${sensor}-structure`)}
          >
            데이터 구조 {expandedSections[`${sensor}-structure`] ? '▼' : '▶'}
          </button>
          
          {expandedSections[`${sensor}-structure`] && (
            <div className="data-structure">
              <table className="structure-table">
                <thead>
                  <tr>
                    <th>필드명</th>
                    <th>타입</th>
                    <th>설명</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.values(data.dataStructure)[0].map((field, index) => (
                    <tr key={index}>
                      <td><code>{field.name}</code></td>
                      <td><span className="type-badge">{field.type}</span></td>
                      <td>{field.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const QualitySection = ({ title, data, icon }) => (
    <div className="quality-section">
      <div className="section-header">
        <span className="section-icon">{icon}</span>
        <h3>{title}</h3>
      </div>
      <div className="quality-grid">
        {Object.entries(data).map(([key, value]) => (
          <div key={key} className="quality-item">
            <span className="quality-label">{key}:</span>
            <span className="quality-value">
              {typeof value === 'object' ? JSON.stringify(value) : value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="rosbag-standards">
      {/* 헤더 */}
      <div className="standards-header">
        <h1>🚗 ROSBag 센서 데이터 처리 표준</h1>
        <p>자율주행 데이터 수집 및 분석을 위한 종합 가이드</p>
      </div>

      {/* 탭 네비게이션 */}
      <div className="tab-navigation">
        <TabButton id="overview" label="개요" icon="📋" />
        <TabButton id="sensors" label="센서 표준" icon="🔧" />
        <TabButton id="quality" label="품질 기준" icon="✅" />
        <TabButton id="naming" label="명명 규칙" icon="📝" />
        <TabButton id="compatibility" label="호환성" icon="🔗" />
      </div>

      {/* 탭 내용 */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="overview-content">
            <div className="overview-grid">
              <div className="overview-card">
                <h3>🎯 목적</h3>
                <p>자율주행 차량의 센서 데이터 수집, 저장, 분석을 위한 표준화된 프레임워크 제공</p>
              </div>
              
              <div className="overview-card">
                <h3>📊 지원 센서</h3>
                <div className="sensor-icons">
                  {Object.entries(sensorStandards).map(([key, sensor]) => (
                    <div key={key} className="sensor-icon-item">
                      <span style={{ color: sensor.color }}>{sensor.icon}</span>
                      <span>{sensor.name}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="overview-card">
                <h3>🔄 처리 파이프라인</h3>
                <div className="pipeline-steps">
                  <div >1. 파일 검증</div>
                  <div >2. 데이터 추출</div>
                  <div >3. 품질 분석</div>
                  <div >4. 결과 저장</div>
                </div>
              </div>
              
              <div className="overview-card">
                <h3>📈 성능 목표</h3>
                <ul>
                  <li>처리 속도: {'>'} 50 MB/s</li>
                  <li>메모리 사용: {'<'} 2GB per 1GB bag</li>
                  <li>품질 점수: {'>'} 90%</li>
                  <li>시스템 가동률: {'>'} 99.9%</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'sensors' && (
          <div className="sensors-content">
            <div className="sensors-grid">
              {Object.entries(sensorStandards).map(([key, sensor]) => (
                <SensorCard key={key} sensor={key} data={sensor} />
              ))}
            </div>
          </div>
        )}

        {activeTab === 'quality' && (
          <div className="quality-content">
            <QualitySection 
              title="시간적 일관성" 
              data={qualityStandards.temporal} 
              icon="⏱️" 
            />
            <QualitySection 
              title="공간적 정확도" 
              data={qualityStandards.spatial} 
              icon="🎯" 
            />
            <QualitySection 
              title="신호 품질" 
              data={qualityStandards.signal} 
              icon="📶" 
            />
          </div>
        )}

        {activeTab === 'naming' && (
          <div className="naming-content">
            <div className="naming-section">
              <h3>📝 파일 명명 규칙</h3>
              <div className="naming-pattern">
                <h4>패턴</h4>
                <code className="pattern-code">{namingConvention.pattern}</code>
              </div>
              
              <div className="naming-examples">
                <h4>예시</h4>
                {namingConvention.examples.map((example, index) => (
                  <code key={index} className="example-code">{example}</code>
                ))}
              </div>
              
              <div className="naming-tags">
                <h4>필수 메타데이터 태그</h4>
                <div className="tags-grid">
                  <span className="tag required">location</span>
                  <span className="tag required">scenario</span>
                  <span className="tag required">vehicle_id</span>
                  <span className="tag required">weather</span>
                  <span className="tag required">time_of_day</span>
                </div>
                
                <h4>선택적 메타데이터 태그</h4>
                <div className="tags-grid">
                  <span className="tag optional">driver_type</span>
                  <span className="tag optional">traffic_density</span>
                  <span className="tag optional">road_type</span>
                  <span className="tag optional">special_events</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'compatibility' && (
          <div className="compatibility-content">
            <div className="compatibility-grid">
              <div>
                <h3>🤖 ROS 버전 지원</h3>
                <ul>
                  <li>ROS Kinetic (16.04)</li>
                  <li>ROS Melodic (18.04)</li>
                  <li>ROS Noetic (20.04)</li>
                  <li>ROS2 Foxy (20.04)</li>
                  <li>ROS2 Galactic (20.04)</li>
                  <li>ROS2 Humble (22.04)</li>
                </ul>
              </div>
              
              <div className="compat-section">
                <h3>🏭 센서 제조사</h3>
                <div className="vendor-list">
                  <div className="vendor-category">
                    <strong>LiDAR:</strong> Velodyne, Ouster, Livox, Hesai, Robosense
                  </div>
                  <div className="vendor-category">
                    <strong>Radar:</strong> Continental, Delphi, Bosch, Aptiv
                  </div>
                  <div className="vendor-category">
                    <strong>Camera:</strong> FLIR, Basler, Allied Vision, Point Grey
                  </div>
                  <div className="vendor-category">
                    <strong>IMU:</strong> Xsens, VectorNav, MicroStrain, Novatel
                  </div>
                  <div className="vendor-category">
                    <strong>GPS:</strong> Novatel, u-blox, Trimble, Septentrio
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .rosbag-standards {
          max-width: 1400px;
          margin: 0 auto;
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .standards-header {
          text-align: center;
          margin-bottom: 40px;
          padding: 40px 20px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-radius: 16px;
        }

        .standards-header h1 {
          font-size: 2.5rem;
          margin: 0 0 10px 0;
          font-weight: 700;
        }

        .standards-header p {
          font-size: 1.2rem;
          margin: 0;
          opacity: 0.9;
        }

        .tab-navigation {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 30px;
          background: #f8fafc;
          padding: 8px;
          border-radius: 12px;
        }

        .tab-button {
          background: transparent;
          border: none;
          padding: 12px 20px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          color: #64748b;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .tab-button:hover {
          background: white;
          color: #334155;
        }

        .tab-button.active {
          background: #3b82f6;
          color: white;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
        }

        .tab-icon {
          font-size: 16px;
        }

        .tab-content {
          min-height: 500px;
        }

        /* Overview Styles */
        .overview-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 24px;
        }

        .overview-card {
          background: white;
          padding: 24px;
          border-radius: 12px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0;
        }

        .overview-card h3 {
          margin: 0 0 16px 0;
          font-size: 1.25rem;
          font-weight: 600;
        }

        .sensor-icons {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }

        .sensor-icon-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
        }

        .sensor-icon-item span:first-child {
          font-size: 20px;
        }

        .pipeline-steps {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .step {
          padding: 8px 12px;
          background: #f1f5f9;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
        }

        /* Sensor Cards */
        .sensors-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 24px;
        }

        .sensor-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0;
          border-left: 4px solid;
          overflow: hidden;
        }

        .sensor-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 20px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
        }

        .sensor-icon {
          font-size: 24px;
        }

        .sensor-header h3 {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 600;
        }

        .sensor-content {
          padding: 20px;
        }

        .info-section {
          margin-bottom: 20px;
        }

        .info-section h4 {
          margin: 0 0 12px 0;
          font-size: 1rem;
          font-weight: 600;
          color: #374151;
        }

        .topic-list {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .topic-name {
          background: #f1f5f9;
          padding: 6px 10px;
          border-radius: 6px;
          font-family: 'Courier New', monospace;
          font-size: 13px;
          color: #1e40af;
        }

        .message-types {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .message-type {
          background: #ecfdf5;
          color: #065f46;
          padding: 6px 10px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
        }

        .specifications {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .spec-item {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid #f1f5f9;
        }

        .spec-label {
          font-weight: 500;
          color: #6b7280;
          text-transform: capitalize;
        }

        .spec-value {
          font-weight: 500;
          color: #1f2937;
        }

        .expand-button {
          background: #f3f4f6;
          border: none;
          padding: 8px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          color: #374151;
          width: 100%;
          text-align: left;
        }

        .expand-button:hover {
          background: #e5e7eb;
        }

        .data-structure {
          margin-top: 12px;
          background: #f9fafb;
          padding: 16px;
          border-radius: 8px;
        }

        .structure-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }

        .structure-table th {
          background: #f3f4f6;
          padding: 8px 12px;
          text-align: left;
          font-weight: 600;
          color: #374151;
          border-bottom: 1px solid #e5e7eb;
        }

        .structure-table td {
          padding: 8px 12px;
          border-bottom: 1px solid #f1f5f9;
        }

        .type-badge {
          background: #dbeafe;
          color: #1e40af;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
        }

        /* Quality Section */
        .quality-content {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .quality-section {
          background: white;
          padding: 24px;
          border-radius: 12px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0;
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;
        }

        .section-icon {
          font-size: 24px;
        }

        .section-header h3 {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 600;
        }

        .quality-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 16px;
        }

        .quality-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 12px;
          background: #f8fafc;
          border-radius: 8px;
        }

        .quality-label {
          font-size: 14px;
          font-weight: 500;
          color: #6b7280;
          text-transform: capitalize;
        }

        .quality-value {
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
        }

        /* Naming Section */
        .naming-content {
          background: white;
          padding: 24px;
          border-radius: 12px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }

        .naming-section h3 {
          margin: 0 0 24px 0;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .naming-pattern {
          margin-bottom: 24px;
        }

        .naming-pattern h4 {
          margin: 0 0 8px 0;
          font-size: 1rem;
          font-weight: 600;
        }

        .pattern-code {
          display: block;
          background: #1f2937;
          color: #f9fafb;
          padding: 12px 16px;
          border-radius: 8px;
          font-family: 'Courier New', monospace;
          font-size: 14px;
          overflow-x: auto;
        }

        .naming-examples {
          margin-bottom: 24px;
        }

        .naming-examples h4 {
          margin: 0 0 12px 0;
          font-size: 1rem;
          font-weight: 600;
        }

        .example-code {
          display: block;
          background: #f1f5f9;
          color: #1e40af;
          padding: 8px 12px;
          border-radius: 6px;
          font-family: 'Courier New', monospace;
          font-size: 13px;
          margin-bottom: 6px;
        }

        .naming-tags h4 {
          margin: 0 0 12px 0;
          font-size: 1rem;
          font-weight: 600;
        }

        .tags-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 20px;
        }

        .tag {
          padding: 6px 12px;
          border-radius: 16px;
          font-size: 12px;
          font-weight: 500;
        }

        .tag.required {
          background: #fef2f2;
          color: #dc2626;
          border: 1px solid #fecaca;
        }

        .tag.optional {
          background: #f0f9ff;
          color: #0369a1;
          border: 1px solid #bae6fd;
        }

        /* Compatibility Section */
        .compatibility-content {
          background: white;
          padding: 24px;
          border-radius: 12px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }

        .compatibility-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 24px;
        }

        .compat-section {
          background: #f8fafc;
          padding: 20px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .compat-section h3 {
          margin: 0 0 16px 0;
          font-size: 1.25rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .compat-section ul {
          margin: 0;
          padding-left: 20px;
        }

        .compat-section li {
          margin-bottom: 8px;
          color: #374151;
        }

        .vendor-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .vendor-category {
          padding: 12px;
          background: white;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
          font-size: 14px;
          line-height: 1.5;
        }

        .vendor-category strong {
          color: #1f2937;
          margin-right: 8px;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .rosbag-standards {
            padding: 16px;
          }

          .standards-header {
            padding: 24px 16px;
          }

          .standards-header h1 {
            font-size: 2rem;
          }

          .standards-header p {
            font-size: 1rem;
          }

          .tab-navigation {
            flex-direction: column;
          }

          .tab-button {
            justify-content: center;
          }

          .overview-grid,
          .sensors-grid {
            grid-template-columns: 1fr;
          }

          .quality-grid {
            grid-template-columns: 1fr;
          }

          .compatibility-grid {
            grid-template-columns: 1fr;
          }

          .sensor-card {
            min-width: unset;
          }

          .spec-item {
            flex-direction: column;
            gap: 4px;
            align-items: flex-start;
          }

          .structure-table {
            font-size: 12px;
          }

          .structure-table th,
          .structure-table td {
            padding: 6px 8px;
          }
        }

        @media (max-width: 480px) {
          .sensor-icons {
            grid-template-columns: 1fr;
          }

          .pattern-code,
          .example-code {
            font-size: 12px;
            overflow-x: scroll;
          }

          .tags-grid {
            flex-direction: column;
          }

          .tag {
            text-align: center;
          }
        }

        /* Dark mode support */
        @media (prefers-color-scheme: dark) {
          .rosbag-standards {
            background-color: #0f172a;
            color: #f1f5f9;
          }

          .overview-card,
          .sensor-card,
          .quality-section,
          .naming-content,
          .compatibility-content {
            background: #1e293b;
            border-color: #334155;
          }

          .sensor-header {
            background: #334155;
            border-color: #475569;
          }

          .tab-navigation {
            background: #1e293b;
          }

          .tab-button {
            color: #94a3b8;
          }

          .tab-button:hover {
            background: #334155;
            color: #f1f5f9;
          }

          .tab-button.active {
            background: #3b82f6;
            color: white;
          }

          .topic-name {
            background: #334155;
            color: #60a5fa;
          }

          .message-type {
            background: #064e3b;
            color: #34d399;
          }

          .expand-button {
            background: #374151;
            color: #f1f5f9;
          }

          .expand-button:hover {
            background: #4b5563;
          }

          .data-structure {
            background: #374151;
          }

          .structure-table th {
            background: #4b5563;
            color: #f1f5f9;
            border-color: #6b7280;
          }

          .structure-table td {
            border-color: #4b5563;
          }

          .quality-item,
          .compat-section {
            background: #334155;
            border-color: #475569;
          }

          .vendor-category {
            background: #475569;
            border-color: #64748b;
          }

          .pattern-code {
            background: #0f172a;
            color: #f1f5f9;
          }

          .example-code {
            background: #334155;
            color: #60a5fa;
          }
        }

        /* Print styles */
        @media print {
          .tab-navigation {
            display: none;
          }

          .rosbag-standards {
            max-width: none;
            padding: 0;
          }

          .overview-grid,
          .sensors-grid,
          .quality-grid,
          .compatibility-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }

          .sensor-card,
          .overview-card,
          .quality-section {
            break-inside: avoid;
            box-shadow: none;
            border: 1px solid #d1d5db;
          }

          .standards-header {
            background: none;
            color: #000;
            border: 1px solid #d1d5db;
          }
        }

        /* Accessibility improvements */
        .tab-button:focus {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }

        .expand-button:focus {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }

        /* Animation for smooth transitions */
        .tab-content {
          animation: fadeIn 0.3s ease-in-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .data-structure {
          animation: slideDown 0.3s ease-out;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            max-height: 0;
          }
          to {
            opacity: 1;
            max-height: 500px;
          }
        }

        /* Hover effects for better interactivity */
        .sensor-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
          transition: all 0.3s ease;
        }

        .overview-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
          transition: all 0.3s ease;
        }

        /* Loading states */
        .loading {
          opacity: 0.6;
          pointer-events: none;
        }

        /* Error states */
        .error {
          border-left-color: #ef4444;
          background-color: #fef2f2;
        }

        .error-message {
          color: #dc2626;
          font-size: 14px;
          margin-top: 8px;
        }

        /* Success states */
        .success {
          border-left-color: #10b981;
          background-color: #f0fdf4;
        }

        /* Utility classes */
        .text-center {
          text-align: center;
        }

        .text-left {
          text-align: left;
        }

        .text-right {
          text-align: right;
        }

        .font-mono {
          font-family: 'Courier New', monospace;
        }

        .font-bold {
          font-weight: 700;
        }

        .font-semibold {
          font-weight: 600;
        }

        .text-sm {
          font-size: 14px;
        }

        .text-xs {
          font-size: 12px;
        }

        .mt-2 {
          margin-top: 8px;
        }

        .mb-2 {
          margin-bottom: 8px;
        }

        .p-2 {
          padding: 8px;
        }

        .rounded {
          border-radius: 6px;
        }

        .shadow {
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
      `}</style>
    </div>
  );
};

export default Standard;